/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { CommonModule } from '@angular/common';
import {
	Component,
	Input,
	OnInit,
	inject,
	ChangeDetectionStrategy,
	Output,
	EventEmitter,
	DestroyRef,
	OnDestroy,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import {
	BehaviorSubject,
	catchError,
	of,
	takeUntil,
	tap,
	switchMap,
	timer,
	map,
	filter,
	withLatestFrom,
	share,
} from 'rxjs';
import { SecureAssetService } from 'src/app/services/secure-asset.service';
import { MaterialModule } from '../material/material-module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { LoggerServiceFactory } from '../logging/LoggerServiceFactory';

/**
 * This component is used to securely load and display images with a fallback mechanism.
 * It handles loading states, errors, and provides a skeleton image while the main image is loading.
 * It also allows for retrying the image load on error.
 */
export enum SecureImageState {
	Loading = 'loading',
	Loaded = 'loaded',
	Error = 'error',
}

@Component({
	selector: 'secure-image',
	standalone: true,
	imports: [CommonModule, MaterialModule],
	templateUrl: './secure-image.component.html',
	styleUrls: ['./secure-image.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecureImage implements OnInit, OnDestroy {
	private readonly secureAssetService = inject(SecureAssetService);
	private readonly destroyRef = inject(DestroyRef);
	private logger = LoggerServiceFactory.create('SecureImage');
	private sanitizer = inject(DomSanitizer);
	private readonly reloadTrigger$ = new BehaviorSubject<number>(0);

	// Input properties
	@Input({ required: true }) src!: string;
	@Input({ required: true }) alt!: string;
	@Input() skeletonSrc?: string;
	@Input() timeoutDuration = 10000;

	@Output() public imageClick = new EventEmitter();

	public appStrings = appStrings;

	// State management
	public imageState$ = new BehaviorSubject<SecureImageState>(SecureImageState.Loading);
	public imageUrl$ = new BehaviorSubject<SafeUrl | null>(null);

	ngOnInit(): void {
		this.initializeSkeleton();
		this.initializeImageLoading();
	}

	ngOnDestroy(): void {
		this.imageState$.complete();
		this.imageUrl$.complete();
		this.reloadTrigger$.complete();
	}

	/**
	 * Retry loading the image
	 */
	public retry(): void {
		this.imageState$.next(SecureImageState.Loading);
		this.initializeSkeleton();
		this.reloadTrigger$.next(this.reloadTrigger$.value + 1);
	}

	public onImageClickEvent(): void {
		this.imageClick.emit();
	}

	/**
	 * Initialize image with skeleton placeholder
	 */
	private initializeSkeleton(): void {
		const skeletonUrl = this.skeletonSrc ? this.sanitizer.bypassSecurityTrustUrl(this.skeletonSrc) : null;
		this.imageUrl$.next(skeletonUrl);
	}

	private initializeImageLoading(): void {
		const ob$ = this.reloadTrigger$.pipe(
			tap(() => {
				this.imageState$.next(SecureImageState.Loading);
			}),
			switchMap((__) =>
				this.secureAssetService.loadAsset(this.src).pipe(
					catchError((error) => {
						this.logger.error('Error loading image', error);
						this.imageState$.next(SecureImageState.Error);
						return of(null);
					})
				)
			),
			share(),
			takeUntilDestroyed(this.destroyRef)
		);

		this.reloadTrigger$
			.pipe(
				switchMap((__) => timer(this.timeoutDuration)),
				takeUntil(ob$.pipe(filter((result) => result !== null))), // Cancel timeout only when image loads successfully
				takeUntilDestroyed(this.destroyRef),
				withLatestFrom(this.imageState$),
				filter(([_, state]) => state !== SecureImageState.Loaded),
				map(() => SecureImageState.Error)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.imageState$));

		ob$.subscribe(subjectSubscribeWithoutComplete(this.imageUrl$));
		ob$
			.pipe(map((result) => (result !== null ? SecureImageState.Loaded : this.imageState$.value)))
			.subscribe(subjectSubscribeWithoutComplete(this.imageState$));
	}
}
